import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useSelector } from 'react-redux'
import numeral from 'numeral'
import moment from 'moment'
import cn from 'classnames'

import Table from '../../../Invoices/Table'

import styles from './styles.module.scss'

import { getCurrency } from '../../../../helpers/currencies'

const columnHelper = createColumnHelper()

const formatNumber = (value, currency) => {
  if (value === null || value === undefined || isNaN(value) || value === Infinity) {
    return `0 ${currency}`
  }
  return `${numeral(value).format('0,0.[00]')} ${currency}`
}

const getDuration = (minutes) => {
  const duartion = moment.duration(minutes, 'minutes')
  const hours = Math.floor(duartion.asHours())
  const mins = duartion.minutes()
  return `${hours}h ${mins}m`
}

const EmployeeCost = ({employees, sales, totalSales, targetEmployeeCostPercentage}) => {
  const { t } = useTranslation()

  const companyInfo = useSelector(state => state.company.companyInfo)
  const currency = getCurrency(companyInfo?.currency)

  

  const data = useMemo(() => {
    const employeesBySkill = employees.reduce((acc, employee) => {
      const skillName = employee.skill || 'Uncategorized'
      return {
        ...acc,
        [skillName]: acc[skillName] ? [...acc[skillName], employee] : [employee],
      }
    }, {})
    return Object.entries(employeesBySkill).map(([skill, employees]) => {
      const {cost, duration} = employees.reduce((acc, employee) => {
        return {
          ...acc,
          duration: acc.duration + (employee.work_duration || 0),
          cost: acc.cost + ((employee.profitability?.cost || 0) * Number(employee.em_status || 1)) * (employee.work_duration / 60),
          
        }
      }, {
        cost: 0,
        duration: 0,
      })
      return {
        name: skill,
        quantity: employees.length,
        duration: duration,
        avgRate: employees.length > 0 ? employees.reduce((acc, employee) => acc + (employee.profitability?.cost || 0) * Number(employee.em_status || 1), 0) / employees.length : 0,
        cost: cost,
        margin: totalSales > 0 ? cost / totalSales * 100 : 0,
        
      }
    })
  }, [employees, totalSales])

  const totals = useMemo(() => {
    return employees.reduce((acc, employee) => {
      return {
        ...acc,
        duration: acc.duration + (employee.work_duration || 0),
        cost: acc.cost + ((employee.profitability?.cost || 0) * Number(employee.em_status || 1)) * (employee.work_duration / 60),
      }
    }, {
      duration: 0,
      cost: 0,
    })
  }, [employees])

  // const totalSalesAmount = useMemo(() => {
  //   return sales.reduce((acc, sale) => acc + (sale.DishDiscountSumInt || 0), 0)
  // }, [sales])

  const columns = useMemo(() => ([
    columnHelper.accessor('name', {
      id: 'name',
      header: t('Skill'),
      enableSorting: false,
      cell: ({ getValue }) => {
        return getValue()
      }
    }),
    columnHelper.accessor('quantity', {
      id: 'quantity',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
      header: t('Employees'),
    }),
    columnHelper.accessor('duration', {
      id: 'duration',
      enableSorting: false,
      cell: ({ getValue }) => getDuration(getValue()),
      header: t('Hours'),
    }),
    columnHelper.accessor('avgRate', {
      id: 'avgRate',
      enableSorting: false,
      cell: ({ getValue }) => formatNumber(getValue(), currency.symbol),
      header: t('Avg. rate'),
    }),
    columnHelper.accessor('cost', {
      id: 'cost',
      enableSorting: false,
      cell: ({ getValue }) => formatNumber(getValue(), currency.symbol),
      header: t('Cost'),
    }),
    columnHelper.accessor('margin', {
      id: 'margin',
      enableSorting: false,
      cell: ({ getValue }) => {
        const value = numeral(getValue()).format('0.[00]') * 1
        return (
          <span className={cn({[styles.negative]: value && value > targetEmployeeCostPercentage, [styles.positive]: value && value < targetEmployeeCostPercentage})}>{`${value}%`}</span>
        )
      },
      header: t('Margin %'),
    }),
  ]), [t, currency.symbol, targetEmployeeCostPercentage])
  
  const table = useReactTable({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{t('Employee cost by role')}</div>
      </div>
      <Table
        table={table}
        columns={columns}
        wrapperStyle={{flex: 1}}
        headerClassName={styles.tableHeader} />
      <div className={styles.cards}>
        <div className={styles.card}>
          <div className={styles.cardTitle}>{ t('Sales / working hours') }</div>
          <div className={styles.cardValue}>{ formatNumber(totalSales / (totals.duration / 60), currency.symbol) }</div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardTitle}>{ t('Sales / employee') }</div>
          <div className={styles.cardValue}>{ formatNumber(totalSales / data.reduce((acc, item) => acc + item.quantity, 0), currency.symbol) }</div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardTitle}>{ t('Total hours') }</div>
          <div className={styles.cardValue}>{ getDuration(totals.duration) }</div>
        </div>
      </div>
    </div>
  )
}

export default EmployeeCost